import React from 'react'
import styled, { css } from 'styled-components'
import breakpoint from 'src/utils/styledComponentsBreakpoint'
import { colors, margin, marginSizes } from '@jarvis.oddle/core/theme/default'
import Flex from '@jarvis.oddle/core/components/Flex'
import Modal, { CloseButton } from '@jarvis.oddle/core/components/Modal'
import { H3, H4, LinkText, SmallText } from '@jarvis.oddle/core/components/Typo'
import Div from '@jarvis.oddle/core/components/Div'
import Card from '@jarvis.oddle/core/components/Card'
import Button from '@jarvis.oddle/core/components/Button'
import IconPickup from '@jarvis.oddle/core/components/Icons/IconPickup'
import IconDelivery from '@jarvis.oddle/core/components/Icons/IconDelivery'
import pickUpDeliveryFlow from 'src/flows/pickUpDeliveryFlow'
import Loading from '@jarvis.oddle/core/components/Loading'
import {
  storeOrderToDeliveryAddress,
  toDeliveryAddress
} from '@jarvis.oddle/core/tools/address'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'
import useAppSelector from 'src/hooks/useAppSelector'
import { selectOrder } from 'src/redux/order'
import { selectUser } from 'src/redux/user'
import { selectDetailMenuSite, selectMenuMode } from 'src/redux/meta'
import {
  syncDeliveryLocationAction,
  syncMethodAction
} from 'src/redux/order/action'
import useAppDispatch from 'src/hooks/useAppDispatcher'
import { useRouter } from 'next/router'
import { redirect } from 'src/utils/routes'
import modalController, {
  makeRouteControlledModal
} from 'src/decorators/ModalController'

const IconContainer = styled(Div)`
  flex-shrink: 0;
`

const iconStyles = css`
  width: 80px;
  height: 80px;
  ${breakpoint('desktop')`
    width: 100px;
    height: 100px;
  `}
`

const StyledIconPickup = styled(IconPickup).attrs((props) => ({
  fill: props.theme.buttonColor || colors.brandPrimary
}))`
  ${iconStyles}
`

const StyledIconDelivery = styled(IconDelivery).attrs((props) => ({
  fill: props.theme.buttonColor || colors.brandPrimary
}))`
  ${iconStyles}
`

const MethodContainer = styled(Card)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 157px;
  cursor: pointer;
  margin-bottom: 0;
  margin-right: ${(props) =>
    props.withoutMargin ? '0' : margin[marginSizes.medium]};
  ${breakpoint('desktop')`
    width: 190px;
  `}
`

const MethodButton = styled(Button)`
  margin-top: auto;
`

const Wrapper = styled(Div)`
  position: relative;
`

const StyledFlex = styled(Flex).attrs(() => ({
  justifyContent: 'center',
  flexDirection: 'row'
}))`
  width: 100%;
`

const LoadingOverlay = styled(Flex).attrs(() => ({
  justifyContent: 'center',
  alignItems: 'center'
}))`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
`

const StyledLinkText = styled(LinkText).attrs((props) => ({
  color: props.theme.colorSectionHeadingsFont
}))``

const PickupDeliverySelectionModal = ({
  modal
}: {
  modal: {
    show: boolean
    onClose: (handler?) => void
    onClosed: () => void
  }
}) => {
  const dispatch = useAppDispatch()
  const { t } = React.useContext(TranslationContext)
  const [isLoading, setIsLoading] = React.useState(false)
  const storeOrder = useAppSelector(selectOrder).storeOrder
  const user = useAppSelector(selectUser)
  const { hasPickup, hasDelivery } = useAppSelector(selectDetailMenuSite) || {}
  const router = useRouter()
  const menuMode = useAppSelector(selectMenuMode)

  const clearRoute = () =>
    redirect({
      url: router.pathname,
      as: router.pathname.replace('[locale]', router.query.locale as string),
      scroll: false,
      shallow: true
    })

  const goToNextFlowState = ({ isPickup, skipAddress }) => {
    clearRoute()
    if (isPickup) {
      pickUpDeliveryFlow.send('pickupStoreSelection')
    } else if (skipAddress) {
      pickUpDeliveryFlow.send('dateTimeSelection')
    } else {
      pickUpDeliveryFlow.send('deliverySelectAddress')
    }
  }

  const syncMethod = async ({ method }) => {
    if (menuMode === 'preview') {
      modalController.showModal('ErrorModal', {
        error: t('Cannot add item to cart on preview mode')
      })
      setIsLoading(false)
      return
    }
    let skipAddress = !!storeOrder.data.deliveryAddress // true = here's delivery address saved to store order on client-side

    if (
      (storeOrder.data.isPickup && method === 'delivery') ||
      (!storeOrder.data.isPickup && method === 'pickup')
    ) {
      setIsLoading(true)
      const response = await dispatch(syncMethodAction({ method }))

      // Sync delivery location action need to be done after sync method action,
      // otherwise there'll be issue with getting correct time slots for delivery.
      if (method === 'delivery') {
        const hasPreferredDeliveryAddress = !!user.data.preferredDeliveryAddress

        let address

        if (!skipAddress && hasPreferredDeliveryAddress) {
          // If delivery address is not set in store order & there's preferred delivery address
          address = toDeliveryAddress(user.data.preferredDeliveryAddress)
        } else if (skipAddress) {
          // If there is delivery address set in store order
          address = storeOrderToDeliveryAddress(storeOrder.data)
        }

        skipAddress = false // Make sure that the address dialog is not skipped if anything went wrong

        // Always sync delivery address since backend won't save delivery request when user toggle between pick up & delivery
        if (address) {
          const syncDeliveryLocation = await dispatch(
            syncDeliveryLocationAction({ address })
          )

          if (
            syncDeliveryLocation.meta.requestStatus === 'fulfilled' &&
            syncDeliveryLocation.payload.payload.ok
          ) {
            skipAddress = true
          }
        }
      }

      if (
        response.meta.requestStatus === 'fulfilled' &&
        response.payload.payload.ok
      ) {
        goToNextFlowState({ isPickup: method === 'pickup', skipAddress })
      } else {
        modalController.showModal('ErrorModal', {
          error: t(response.payload.payload.error)
        })
      }
      setIsLoading(false)
    } else {
      goToNextFlowState({ isPickup: method === 'pickup', skipAddress })
    }
  }

  const openPickUpModal = () => syncMethod({ method: 'pickup' })

  const openDeliveryModal = () => syncMethod({ method: 'delivery' })

  const onSkipMenuClicked = () => pickUpDeliveryFlow.finish()

  const onCloseModal = () => {
    clearRoute()
    modal.onClose()
  }

  return (
    <Modal {...modal} slideInAnimation='bottom'>
      <Modal.Container>
        <Modal.Header>
          <H4 bold margin='none'>
            {t('Select your dining preference')}
          </H4>
          <CloseButton onClick={onCloseModal} />
        </Modal.Header>
        <Modal.Body withoutFooter padding='medium'>
          <Wrapper>
            <StyledFlex>
              {hasPickup && (
                <MethodContainer role='button' onClick={openPickUpModal}>
                  <H3 margin='none' bold>
                    {t('Pickup')}
                  </H3>
                  <IconContainer>
                    <StyledIconPickup />
                  </IconContainer>
                  <SmallText textAlign='center'>
                    {t('Self-collect and beat the queue')}
                  </SmallText>
                  <MethodButton>{t('Select')}</MethodButton>
                </MethodContainer>
              )}
              {hasDelivery && (
                <MethodContainer
                  role='button'
                  onClick={openDeliveryModal}
                  withoutMargin={hasPickup}
                >
                  <H3 margin='none' bold>
                    {t('Delivery')}
                  </H3>
                  <IconContainer>
                    <StyledIconDelivery />
                  </IconContainer>
                  <SmallText textAlign='center'>
                    {t('Delivered right to your doorstep')}
                  </SmallText>
                  <MethodButton>{t('Select')}</MethodButton>
                </MethodContainer>
              )}
            </StyledFlex>
            <Flex
              justifyContent='center'
              marginTop={marginSizes.extraLarge}
              marginBottom={marginSizes.small}
            >
              <StyledLinkText onClick={onSkipMenuClicked} bold>
                {t(`I'm just browsing`)}
              </StyledLinkText>
            </Flex>
            {isLoading && (
              <LoadingOverlay>
                <Loading />
              </LoadingOverlay>
            )}
          </Wrapper>
        </Modal.Body>
      </Modal.Container>
    </Modal>
  )
}

export default makeRouteControlledModal(
  PickupDeliverySelectionModal,
  'PickupDeliverySelectionModal',
  ({ query }) => query.selectDiningPreference === 'true',
  (router) =>
    redirect({
      url: '/[locale]',
      as: `/${router.query.locale}`,
      scroll: false,
      shallow: true
    })
)
